import React, { useState, useEffect } from 'react';
import './styles.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PremiumProvider, usePremium } from './contexts/PremiumContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useTranslation } from './hooks/useTranslation';
import { trackPageView, trackEvent, setUserId, resetUserId } from './utils/matomo';
import FeatureCards from './components/FeatureCards';
import LanguageSwitcher from './components/LanguageSwitcher';
import SimpleFriendsList from './components/SimpleFriendsList';
import Dashboard from './components/Dashboard';
import DeepeningRelationships from './components/journeys/DeepeningRelationships';
import JourneysOverview from './components/journeys/JourneysOverview';
import FriendshipTracker from './components/FriendshipTracker';
import FriendProfile from './components/FriendProfile';
import AuthModal from './components/auth/AuthModal';
import UserProfile from './components/auth/UserProfile';
import SubscriptionManagement from './components/SubscriptionManagement';
import FriendshipNeedsAssessment from './components/FriendshipNeedsAssessment';
import Footer from './components/Footer';

function AppContent() {
  const [activeView, setActiveView] = useState('home'); // 'home', 'journey', 'tracker', 'journeys-overview', 'subscription', 'friend-profile', 'needs-assessment'
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [paymentMessage, setPaymentMessage] = useState(null);
  const { currentUser } = useAuth();
  const { handlePaymentSuccess } = usePremium();
  const { t } = useTranslation();

  // Handle payment redirect parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    console.log('Payment redirect detected:', { payment, sessionId });
    
    if (payment === 'success') {
      console.log('Payment success detected, setting message and calling handlePaymentSuccess');
      setPaymentMessage({
        type: 'success',
        text: t('paymentSuccessful'),
        sessionId: sessionId
      });
      // Navigate to dashboard if not already there
      if (activeView !== 'home') {
        setActiveView('home');
      }
      // Handle the successful payment
      if (sessionId) {
        console.log('Calling handlePaymentSuccess with sessionId:', sessionId);
        // Ensure currentUser is available before calling
        if (currentUser) {
          handlePaymentSuccess(sessionId).catch((error) => {
            console.error('Error in handlePaymentSuccess:', error);
          });
        } else {
          console.warn('⚠️ No currentUser when trying to handle payment success, will retry');
          // Retry after a short delay
          setTimeout(() => {
            if (currentUser && sessionId) {
              console.log('Retrying handlePaymentSuccess...');
              handlePaymentSuccess(sessionId).catch((error) => {
                console.error('Error in handlePaymentSuccess retry:', error);
              });
            }
          }, 1000);
        }
      }
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === 'cancelled') {
      setPaymentMessage({
        type: 'info',
        text: t('paymentCancelled'),
        sessionId: sessionId
      });
      // Navigate to home if not already there
      if (activeView !== 'home') {
        setActiveView('home');
      }
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [handlePaymentSuccess, currentUser, activeView, t]);

  // Redirect to home when user logs out while on protected pages
  useEffect(() => {
    if (!currentUser && (activeView === 'journey' || activeView === 'tracker' || activeView === 'journeys-overview' || activeView === 'subscription' || activeView === 'needs-assessment')) {
      setActiveView('home');
      setSelectedTopic(null);
    }
  }, [currentUser, activeView]);

  // Track page views with Matomo when activeView changes
  useEffect(() => {
    const viewNames = {
      'home': 'Home',
      'journey': 'Journey',
      'tracker': 'Friendship Tracker',
      'journeys-overview': 'Journeys Overview',
      'subscription': 'Subscription Management',
      'friend-profile': 'Friend Profile',
      'needs-assessment': 'Friendship Goals'
    };
    
    const pageTitle = viewNames[activeView] || 'Unknown Page';
    trackPageView(pageTitle, `/${activeView}`);
  }, [activeView]);

  // Track user authentication events
  useEffect(() => {
    if (currentUser) {
      // Set user ID for tracking
      setUserId(currentUser.uid);
      trackEvent('User', 'Login', 'User logged in');
    } else {
      // Reset user ID on logout
      resetUserId();
      trackEvent('User', 'Logout', 'User logged out');
    }
  }, [currentUser]);

  const handleTopicSelect = (topic) => {
    // Check if user is authenticated for protected features
    if ((topic.id === 'deepening-relationships' || topic.id === 'friendship-tracker') && !currentUser) {
      setAuthMode('signup');
      setShowAuthModal(true);
      return;
    }

    if (topic.id === 'deepening-relationships') {
      setSelectedTopic(topic);
      setActiveView('journeys-overview');
    } else if (topic.id === 'friendship-tracker') {
      setSelectedTopic(topic);
      setTrackerInitialTab('friends');
      setActiveView('tracker');
    }
    // Removed chat functionality
  };

  const handleBackToHome = () => {
    setActiveView('home');
    setSelectedTopic(null);
    setShowFriendDetailsOnLoad(false);
  };

  const handleBackToJourneys = () => {
    setActiveView('journeys-overview');
    setSelectedTopic(null);
    setShowFriendDetailsOnLoad(false);
  };

  const [focusFriendId, setFocusFriendId] = useState(null);
  const [journeyFriendId, setJourneyFriendId] = useState(null);
  const [journeyId, setJourneyId] = useState(null);
  const [showFriendDetailsOnLoad, setShowFriendDetailsOnLoad] = useState(false);
  const [showScheduleAppointmentOnLoad, setShowScheduleAppointmentOnLoad] = useState(false);
  const [selectedFriendForAppointment, setSelectedFriendForAppointment] = useState(null);
  const [showGoalsOnLoad, setShowGoalsOnLoad] = useState(false);
  const [selectedFriendForGoals, setSelectedFriendForGoals] = useState(null);
  const [showFriendshipTypeOnLoad, setShowFriendshipTypeOnLoad] = useState(false);
  const [selectedFriendForType, setSelectedFriendForType] = useState(null);
  const [previousView, setPreviousView] = useState(null);
  const [trackerInitialTab, setTrackerInitialTab] = useState('friends');

  const handleFriendClick = (friend) => {
    setPreviousView(activeView); // Save the current view before navigating
    setFocusFriendId(friend.id);
    setActiveView('friend-profile');
  };

  const handleBackFromFriendProfile = () => {
    // If we came from tracker, go back to tracker, otherwise go to home
    if (previousView === 'tracker') {
      setTrackerInitialTab('friends');
      setActiveView('tracker');
    } else {
      setActiveView('home');
    }
    setPreviousView(null);
    setShowFriendDetailsOnLoad(false);
  };

  const handleJourneyStart = (friendId, specificJourneyId = null) => {
    setJourneyFriendId(friendId);
    setJourneyId(specificJourneyId);
    setActiveView('journey');
  };

  const handleViewFriendDetails = (friendId) => {
    setFocusFriendId(friendId);
    setShowFriendDetailsOnLoad(true);
    setTrackerInitialTab('friends');
    setActiveView('tracker');
  };

  const handleFriendDetailsClosed = () => {
    setShowFriendDetailsOnLoad(false);
  };

  const handleScheduleAppointmentClosed = () => {
    setShowScheduleAppointmentOnLoad(false);
    setSelectedFriendForAppointment(null);
  };

  const handleGoalsClosed = () => {
    setShowGoalsOnLoad(false);
    setSelectedFriendForGoals(null);
  };

  const handleFriendshipTypeClosed = () => {
    setShowFriendshipTypeOnLoad(false);
    setSelectedFriendForType(null);
  };

  const handleSeeAllFriends = () => {
    setTrackerInitialTab('friends');
    setActiveView('tracker');
  };

  const handleViewAppointments = () => {
    setFocusFriendId(null);
    setShowFriendDetailsOnLoad(false);
    setShowScheduleAppointmentOnLoad(false);
    setSelectedFriendForAppointment(null);
    setShowGoalsOnLoad(false);
    setSelectedFriendForGoals(null);
    setShowFriendshipTypeOnLoad(false);
    setSelectedFriendForType(null);
    setTrackerInitialTab('appointments');
    setActiveView('tracker');
  };

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleSubscriptionManagement = () => {
    setActiveView('subscription');
  };

  return (
    <div className="App">
      {/* Header with Auth */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo" onClick={() => setActiveView('home')}>
            <img src="/Upsdowns_logo.png" alt="Ups & Downs" className="logo-image" />
            <span className="logo-text">Ups & Downs</span>
          </div>
          
          {/* Navigation Links */}
          {currentUser && (
            <nav className="main-navigation">
              <button 
                className={`nav-link ${activeView === 'home' ? 'active' : ''}`}
                onClick={() => setActiveView('home')}
              >
                📊 {t('dashboard')}
              </button>
              <button 
                className={`nav-link ${activeView === 'tracker' ? 'active' : ''}`}
                onClick={() => {
                  setTrackerInitialTab('friends');
                  setActiveView('tracker');
                }}
              >
                👫 {t('friendships')}
              </button>
              <button 
                className={`nav-link ${activeView === 'journeys-overview' ? 'active' : ''}`}
                onClick={() => setActiveView('journeys-overview')}
              >
                🛤️ {t('journeys')}
              </button>
              <button 
                className={`nav-link ${activeView === 'needs-assessment' ? 'active' : ''}`}
                onClick={() => setActiveView('needs-assessment')}
              >
                🎯 {t('friendshipGoals')}
              </button>
            </nav>
          )}
          
          <div className="header-actions">
            {currentUser ? (
              <UserProfile onSubscriptionManagement={handleSubscriptionManagement} />
            ) : (
              <>
                <LanguageSwitcher />
                <div className="auth-buttons">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => handleAuthClick('login')}
                  >
                    {t('signIn')}
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleAuthClick('signup')}
                  >
                    {t('getStarted')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Payment Message */}
      {paymentMessage && (
        <div className={`payment-message payment-message-${paymentMessage.type}`}>
          <div className="payment-message-content">
            <span className="payment-message-text">{paymentMessage.text}</span>
            <button 
              className="payment-message-close"
              onClick={() => setPaymentMessage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="app-main">
        {activeView === 'home' ? (
          currentUser ? (
            <Dashboard 
              onFriendClick={handleFriendClick}
              onSeeAllFriends={handleSeeAllFriends}
              onJourneyClick={handleJourneyStart}
              onViewJourneys={() => setActiveView('journeys-overview')}
              onViewNeedsAssessment={() => setActiveView('needs-assessment')}
              onViewAppointments={handleViewAppointments}
            />
          ) : (
            <>
              <SimpleFriendsList 
                onFriendClick={handleFriendClick} 
                onSeeAllFriends={handleSeeAllFriends}
                onJourneyClick={handleJourneyStart}
              />
              <FeatureCards onGetStarted={() => handleAuthClick('signup')} />
            </>
          )
        ) : activeView === 'journey' ? (
          <DeepeningRelationships 
            onBack={handleBackToJourneys}
            friendId={journeyFriendId}
            journeyId={journeyId}
          />
        ) : activeView === 'tracker' ? (
          <FriendshipTracker 
            focusFriendId={focusFriendId}
            onStartJourney={handleJourneyStart}
            showFriendDetailsOnLoad={showFriendDetailsOnLoad}
            onFriendDetailsClosed={handleFriendDetailsClosed}
            onFriendClick={handleFriendClick}
            showScheduleAppointmentOnLoad={showScheduleAppointmentOnLoad}
            selectedFriendForAppointment={selectedFriendForAppointment}
            onScheduleAppointmentClosed={handleScheduleAppointmentClosed}
            showGoalsOnLoad={showGoalsOnLoad}
            selectedFriendForGoalsExternal={selectedFriendForGoals}
            onGoalsClosed={handleGoalsClosed}
            showFriendshipTypeOnLoad={showFriendshipTypeOnLoad}
            selectedFriendForType={selectedFriendForType}
            onFriendshipTypeClosed={handleFriendshipTypeClosed}
            initialActiveTab={trackerInitialTab}
            onBack={handleBackToHome}
          />
        ) : activeView === 'friend-profile' ? (
          <FriendProfile 
            friendId={focusFriendId}
            onBack={handleBackFromFriendProfile}
            onStartJourney={handleJourneyStart}
            onFriendChange={(friendId) => {
              setFocusFriendId(friendId);
              // Stay on friend-profile view, just change the friend
            }}
            onEditProfile={(friend) => {
              setFocusFriendId(friend.id);
              setTrackerInitialTab('friends');
              setActiveView('tracker');
            }}
            onShowInteractionHistory={(friend) => {
              setFocusFriendId(friend.id);
              setShowFriendDetailsOnLoad(true);
              setTrackerInitialTab('friends');
              setActiveView('tracker');
            }}
            onScheduleAppointment={(friend) => {
              setFocusFriendId(friend.id);
              setSelectedFriendForAppointment(friend);
              setShowScheduleAppointmentOnLoad(true);
              setTrackerInitialTab('appointments');
              setActiveView('tracker');
            }}
            onShowGoals={(friend) => {
              setFocusFriendId(friend.id);
              setSelectedFriendForGoals(friend);
              setShowGoalsOnLoad(true);
              setTrackerInitialTab('friends');
              setActiveView('tracker');
            }}
            onSelectFriendshipType={(friend) => {
              setFocusFriendId(friend.id);
              setSelectedFriendForType(friend);
              setShowFriendshipTypeOnLoad(true);
              setTrackerInitialTab('friends');
              setActiveView('tracker');
            }}
          />
        ) : activeView === 'journeys-overview' ? (
          <JourneysOverview 
            onBack={handleBackToHome}
            onStartJourney={handleJourneyStart}
            onViewFriendDetails={handleViewFriendDetails}
          />
        ) : activeView === 'subscription' ? (
          <SubscriptionManagement 
            onBack={handleBackToHome}
          />
        ) : activeView === 'needs-assessment' ? (
          <FriendshipNeedsAssessment 
            onBack={handleBackToHome}
          />
        ) : null}
      </main>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <PremiumProvider>
          <AppContent />
        </PremiumProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;